// «En llamada» while in a call — the client's OWN custom status (shared/API.md §38, 2026-10-17).
// Twin of the native `CallStatusClaim` written from the same contract.
//
// Satori Core also writes custom statuses (📆 `calendar` «En reunión hasta…», 🌴 `palm_tree`
// vacations) and never overwrites one it did not write, so the two take turns without talking:
// - join: claim only over an EMPTY / EXPIRED status or Core's calendar one; anything else (the
//   person's own, a vacation) is left alone and the call is NOT claimed;
// - while in the call: refresh the same body every 5 minutes, only while the status is still ours;
// - leave: clear it only if it is still ours (Core puts «En reunión…» back within a minute).
// The text is Spanish on every UI language — other people read it, and Core's are Spanish too.
import { CustomStatusCodec, CustomStatusDurations, type CustomStatus } from './customStatus';

export const CALL_STATUS_EMOJI = 'headphones';
export const CALL_STATUS_TEXT = 'En llamada';
/** The emoji of Core's calendar statuses — the one status a call may replace. */
export const CORE_CALENDAR_EMOJI = 'calendar';
/** A crashed or killed client leaves the status for at most this long. */
export const CALL_STATUS_TTL_MS = 10 * 60_000;
export const CALL_STATUS_REFRESH_MS = 5 * 60_000;

export interface CallStatusBody {
  emoji: string;
  text: string;
  duration: 'date_and_time';
  expires_at: string;
}

/** The status the store holds (raw `props.customStatus`), parsed; null = none. */
export function current(customStatusJSON: string | null | undefined): CustomStatus | null {
  return CustomStatusCodec.fromPropsJSON(customStatusJSON);
}

/** Ours = exactly the status this rule writes (emoji AND text), whether or not it has expired. */
export function isOurs(status: CustomStatus | null): boolean {
  return !!status && status.emoji === CALL_STATUS_EMOJI && status.text === CALL_STATUS_TEXT;
}

/** At join: may the call take the status? Empty, expired, Core's calendar — or our own leftover. */
export function mayClaim(status: CustomStatus | null, now: number): boolean {
  if (!status) return true;
  if (!CustomStatusCodec.isActive(status, now)) return true;
  if (status.emoji === CORE_CALENDAR_EMOJI) return true;
  return isOurs(status);
}

/** `PUT /users/me/status/custom` body: expires 10 minutes from now, UTC RFC 3339. */
export function body(now: number): CallStatusBody {
  return { emoji: CALL_STATUS_EMOJI, text: CALL_STATUS_TEXT, duration: 'date_and_time', expires_at: CustomStatusDurations.toISO(now + CALL_STATUS_TTL_MS) };
}

export type CallStatusStep = 'claim' | 'release' | null;

/**
 * What a call phase change asks for: reaching `active` (connected) claims; any end — from a
 * connected call back to `idle` — releases. A join that never connected claimed nothing.
 */
export function step(before: string, after: string): CallStatusStep {
  if (before !== 'active' && after === 'active') return 'claim';
  if (before === 'active' && after !== 'active') return 'release';
  return null;
}

export const CallStatusClaim = { current, isOurs, mayClaim, body, step } as const;
